import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Download, AlertTriangle, FileText, Building, ShieldCheck, Files, Mail, Settings2 } from "lucide-react";

export default function Tax1099MiscPage() {
  const { propertyId } = useProperty();
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filer, setFiler] = useState(null);
  const [filerReady, setFilerReady] = useState(false);
  const [filerErrors, setFilerErrors] = useState([]);

  const load = async () => {
    if (!propertyId) return;
    setLoading(true);
    try {
      const [d, f] = await Promise.all([
        api.get(`/admin/tax/1099-misc?year=${year}&property_id=${propertyId}`),
        api.get(`/admin/tax/1099-misc/filer/${propertyId}`).catch(() => ({ data: null })),
      ]);
      setData(d.data);
      if (f.data) {
        setFiler(f.data.filer || {});
        setFilerReady(!!f.data.ready_to_file);
        setFilerErrors(f.data.validation_errors || []);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setLoading(false);
  };

  useEffect(() => { load(); }, [propertyId, year]); // eslint-disable-line react-hooks/exhaustive-deps

  const dl = async (path, mime, filename) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const blob = new Blob([res.data], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      const c1 = res.headers?.["x-payee-count"] || res.headers?.["x-recipient-count"];
      if (c1) toast.success(`Downloaded — ${c1} records included`);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.response?.statusText || "Download failed";
      toast.error(String(msg).slice(0, 240));
    }
  };
  const downloadCsv = () => dl(`/admin/tax/1099-misc.csv?year=${year}&property_id=${propertyId}`, "text/csv", `1099-misc-${year}.csv`);
  const downloadPdf = () => dl(`/admin/tax/1099-misc.pdf?year=${year}&property_id=${propertyId}`, "application/pdf", `1099-misc-${year}.pdf`);
  const downloadFire = () => dl(`/admin/tax/1099-misc.fire.txt?year=${year}&property_id=${propertyId}`, "text/plain", `IRSTAX-1099MISC-${year}.txt`);
  const downloadIrisXml = () => dl(`/admin/tax/1099-misc.iris.xml?year=${year}&property_id=${propertyId}`, "application/xml", `IRIS-1099MISC-${year}.xml`);
  const downloadIrisCsv = () => dl(`/admin/tax/1099-misc.iris.csv?year=${year}&property_id=${propertyId}`, "text/csv", `IRIS-1099MISC-${year}.csv`);
  const downloadMail = () => dl(`/admin/tax/1099-misc/mail-merge.pdf?year=${year}&property_id=${propertyId}`, "application/pdf", `1099-misc-recipient-copies-${year}.pdf`);

  return (
    <div className="space-y-6" data-testid="tax-1099-misc-page">
      <PageHeader
        eyebrow="Tax & Compliance"
        title="1099-MISC Owner Distributions"
        subtitle="IRS Box 1 (Rents) for property owners. Threshold $600/yr per owner. Complements 1099-NEC for vendor payments."
        actions={
          <div className="flex flex-wrap gap-2">
            <Input type="number" value={year} onChange={(e) => setYear(e.target.value)} className="w-24" data-testid="year-input" />
            <Button onClick={load} data-testid="refresh-btn" variant="outline" className="border-white/10">Refresh</Button>
            <FilerDialog propertyId={propertyId} filer={filer} onSaved={load} filerReady={filerReady} />
            <Button variant="outline" onClick={downloadCsv} className="gap-2 border-white/10" data-testid="download-csv"><Download size={14} /> CSV</Button>
            <Button variant="outline" onClick={downloadPdf} className="gap-2 border-white/10" data-testid="download-pdf"><FileText size={14} /> Summary PDF</Button>
            <Button variant="outline" onClick={downloadMail} disabled={!filerReady}
                    className="gap-2 border-white/10 disabled:opacity-40" data-testid="download-mail-merge">
              <Mail size={14} /> Mail merge (Copy B)
            </Button>
            <Button onClick={downloadFire} disabled={!filerReady}
                    className="gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="download-fire">
              <Files size={14} /> FIRE .txt (TY ≤ 2025)
            </Button>
            <Button onClick={downloadIrisXml} disabled={!filerReady}
                    className="gap-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="download-iris-xml">
              <Files size={14} /> IRIS XML (TY 2026+)
            </Button>
            <Button onClick={downloadIrisCsv} disabled={!filerReady}
                    variant="outline"
                    className="gap-2 border-indigo-500/40 text-indigo-300 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="download-iris-csv">
              <Files size={14} /> IRIS CSV
            </Button>
          </div>
        }
      />

      {!filerReady && (
        <Card className="border-amber-500/30 bg-amber-500/[0.04] p-4" data-testid="filer-warning">
          <div className="flex items-start gap-3">
            <ShieldCheck className="h-5 w-5 text-amber-400 mt-0.5" />
            <div className="flex-1">
              <div className="font-semibold text-amber-200">Filer info required before you can e-file</div>
              <div className="text-xs text-zinc-400 mt-1">
                Configure your IRS Transmitter Control Code (TCC), Payer TIN, and address before generating the FIRE flat file
                or recipient copies. Get a TCC via IRS Form 4419.
              </div>
              {filerErrors.length > 0 && (
                <ul className="text-xs text-amber-300 mt-2 list-disc list-inside">
                  {filerErrors.slice(0, 5).map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              )}
            </div>
          </div>
        </Card>
      )}

      {loading && <div className="text-sm text-muted-foreground">Loading…</div>}

      {data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Card className="p-4 border-white/10 bg-card">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Year</div>
              <div className="text-2xl font-semibold mt-1" data-testid="year-label">{data.year}</div>
            </Card>
            <Card className="p-4 border-white/10 bg-card">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Reportable Owners</div>
              <div className="text-2xl font-semibold mt-1 text-emerald-500" data-testid="reportable-count">{data.reportable_count}</div>
              <div className="text-xs text-muted-foreground">≥ ${data.threshold} threshold</div>
            </Card>
            <Card className="p-4 border-white/10 bg-card">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total Reportable</div>
              <div className="text-2xl font-semibold mt-1 font-mono" data-testid="reportable-total">${(data.reportable_total || 0).toLocaleString()}</div>
            </Card>
            <Card className={`p-4 border-white/10 bg-card ${data.missing_tin_count > 0 ? "border-rose-500/40" : ""}`}>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                <AlertTriangle size={12} /> Missing TIN
              </div>
              <div className={`text-2xl font-semibold mt-1 ${data.missing_tin_count > 0 ? "text-rose-400" : ""}`} data-testid="missing-tin">{data.missing_tin_count}</div>
              <div className="text-xs text-muted-foreground">Collect W-9 before Jan 31</div>
            </Card>
          </div>

          <Card className="border-white/10 bg-card overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10">
                  <TableHead>Owner</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>TIN (last 4)</TableHead>
                  <TableHead className="text-right">Statements</TableHead>
                  <TableHead className="text-right">Total Distribution</TableHead>
                  <TableHead>1099 Required</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.rows.length === 0 && (
                  <TableRow className="border-white/5"><TableCell colSpan={6} className="text-center py-8 text-muted-foreground text-sm" data-testid="tax-empty">
                    No distributions marked sent in {data.year}. Generate + mark distributions sent under Owner Portal first.
                  </TableCell></TableRow>
                )}
                {data.rows.map((r) => (
                  <TableRow key={r.owner_id} className="border-white/5" data-testid={`tax-row-${r.owner_id}`}>
                    <TableCell className="font-medium">{r.owner_name}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{r.owner_email}</TableCell>
                    <TableCell className="text-sm font-mono">
                      {r.owner_tin_last4 ? `··${r.owner_tin_last4}` : <span className="text-rose-400">MISSING</span>}
                    </TableCell>
                    <TableCell className="text-right">{r.statement_count}</TableCell>
                    <TableCell className="text-right font-mono">${r.total_distribution.toLocaleString()}</TableCell>
                    <TableCell>
                      {r.above_threshold ? <Badge variant="default" className="bg-emerald-600">Yes</Badge> : <Badge variant="outline">No</Badge>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </>
      )}
    </div>
  );
}

function FilerDialog({ propertyId, filer, onSaved, filerReady }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  useEffect(() => { if (open) setForm(filer || {}); }, [open, filer]);

  const save = async () => {
    if (!propertyId) return toast.error("Pick a property first");
    try {
      await api.post("/admin/tax/1099-misc/filer", { property_id: propertyId, ...form });
      toast.success("Filer info saved");
      setOpen(false);
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className={`border-white/10 gap-2 ${!filerReady ? "border-amber-500/40 text-amber-300" : ""}`} data-testid="filer-btn">
          <Settings2 size={14} /> Filer info {filerReady ? "✓" : ""}
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-card border-white/10 max-w-lg" data-testid="filer-dialog">
        <DialogHeader>
          <DialogTitle>IRS filer settings</DialogTitle>
          <DialogDescription className="text-xs">
            Required for IRS FIRE e-file. TCC is issued via IRS Form 4419 (allow 45 days lead time).
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Transmitter Control Code (5-char)</Label>
            <Input value={form.tcc || ""} onChange={(e) => setForm({ ...form, tcc: e.target.value.toUpperCase() })}
                   placeholder="AB12C" maxLength={5} className="font-mono" data-testid="filer-tcc" />
          </div>
          <div>
            <Label>Payer TIN / EIN (9 digits)</Label>
            <Input value={form.payer_tin || ""} onChange={(e) => setForm({ ...form, payer_tin: e.target.value })}
                   placeholder="12-3456789" className="font-mono" data-testid="filer-tin" />
          </div>
          <div className="col-span-2">
            <Label>Payer legal name</Label>
            <Input value={form.payer_name || ""} onChange={(e) => setForm({ ...form, payer_name: e.target.value })}
                   placeholder="Prestigious Property Management Services LLC" data-testid="filer-name" />
          </div>
          <div className="col-span-2">
            <Label>Street address</Label>
            <Input value={form.payer_address_line || ""} onChange={(e) => setForm({ ...form, payer_address_line: e.target.value })}
                   placeholder="123 Main St" data-testid="filer-street" />
          </div>
          <div>
            <Label>City</Label>
            <Input value={form.payer_city || ""} onChange={(e) => setForm({ ...form, payer_city: e.target.value })}
                   data-testid="filer-city" />
          </div>
          <div>
            <Label>State (2)</Label>
            <Input value={form.payer_state || ""} maxLength={2}
                   onChange={(e) => setForm({ ...form, payer_state: e.target.value.toUpperCase() })}
                   data-testid="filer-state" />
          </div>
          <div>
            <Label>ZIP</Label>
            <Input value={form.payer_zip || ""} onChange={(e) => setForm({ ...form, payer_zip: e.target.value })}
                   className="font-mono" data-testid="filer-zip" />
          </div>
          <div>
            <Label>Contact name</Label>
            <Input value={form.contact_name || ""} onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                   data-testid="filer-contact-name" />
          </div>
          <div>
            <Label>Contact email</Label>
            <Input type="email" value={form.contact_email || ""} onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                   data-testid="filer-contact-email" />
          </div>
          <div>
            <Label>Contact phone</Label>
            <Input value={form.contact_phone || ""} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                   className="font-mono" data-testid="filer-contact-phone" />
          </div>
        </div>
        <div className="text-[10px] text-zinc-500 mt-2">
          <Building className="inline h-3 w-3 mr-1" /> This filer info is stored per-property. Fill it once per PM firm entity.
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="border-white/10">Cancel</Button>
          <Button onClick={save} className="bg-orange-500 hover:bg-orange-600" data-testid="filer-save">Save filer info</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
